import { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '../components/error/ErrorBoundary';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui';
import { Icons } from '../icons';
import { ProtectedRoute } from '../features/auth/components/ProtectedRoute';

// Lazy load pages for code splitting
const AppLayout = lazy(() => import('../components/layout/AppLayout'));

// Auth pages
const LoginPage = lazy(() => import('../features/auth/components/LoginPage'));
const ResetPasswordPage = lazy(() => import('../features/auth/components/ResetPasswordPage'));

// Feature pages
const Dashboard = lazy(() => import('../features/dashboard/Dashboard'));
const BorefieldList = lazy(() => import('../features/borefields/BorefieldList'));
const BorefieldDetail = lazy(() => import('../features/borefields/BorefieldDetail'));
const ReportsView = lazy(() => import('../features/reports/ReportsView'));
const RigsView = lazy(() => import('../features/rigs/RigsView'));
const SettingsView = lazy(() => import('../features/settings/SettingsView'));
const FormationTopsView = lazy(() => import('../features/formation-tops/components/FormationTopsView'));

// Time Reports — 3 URL patterns (PR2-B.4)
// Pattern A: /borefields/:borefieldId/jobs/:jobId/time-reports
// Pattern B: /jobs/:jobId/time-reports
// Pattern C: /time-reports?jobId=<n>  (global filter view)
const TimeReportsView = lazy(() =>
  import('../features/time-reports/components/TimeReportsView').then((m) => ({
    default: m.TimeReportsView,
  })),
);

// Unauthorized page component
const UnauthorizedPage = () => {
  const navigate = useNavigate();
  return (
    <div className="unauthorized-page">
      <div className="unauthorized-content">
        <Icons.Shield />
        <h1>Access Denied</h1>
        <p>You don&apos;t have permission to access this page.</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    </div>
  );
};

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader">
    <LoadingSpinner size="large" />
    <span>Loading...</span>
  </div>
);

// Root layout with suspense (unprotected, for use in protected wrapper)
const RootLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <AppLayout>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </AppLayout>
    </Suspense>
  </ErrorBoundary>
);

// Protected root layout - requires authentication
const ProtectedRootLayout = () => (
  <ProtectedRoute>
    <RootLayout />
  </ProtectedRoute>
);

// Route configuration
// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouter([
  // Auth routes (outside main layout)
  {
    path: '/login',
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <LoginPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    // Password-reset email-link landing. Public (no auth required).
    // The email contains /reset-password?token=...; this page reads the
    // token from the query string and POSTs it to
    // ${VITE_AUTH_URL}/v1/auth/password-reset/confirm together with the
    // new password the user typed.
    path: '/reset-password',
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <ResetPasswordPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: '/unauthorized',
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <UnauthorizedPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  // Main app routes (with layout, protected)
  {
    path: '/',
    element: <ProtectedRootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/borefields" replace />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'borefields',
        children: [
          {
            index: true,
            element: <BorefieldList />,
          },
          {
            path: ':borefieldId',
            element: <BorefieldDetail />,
          },
          {
            path: ':borefieldId/bore/:boreId',
            element: <BorefieldDetail />,
          },
          {
            path: ':borefieldId/bore/:boreId/job/:jobId',
            element: <BorefieldDetail />,
          },
          // Time Reports — Pattern A: /borefields/:borefieldId/jobs/:jobId/time-reports
          {
            path: ':borefieldId/jobs/:jobId/time-reports',
            element: <TimeReportsView />,
          },
        ],
      },
      {
        path: 'reports',
        element: <ReportsView />,
      },
      // Time Reports — Pattern C: /time-reports?jobId=<n>
      {
        path: 'time-reports',
        element: <TimeReportsView />,
      },
      // Time Reports — Pattern B: /jobs/:jobId/time-reports
      {
        path: 'jobs/:jobId/time-reports',
        element: <TimeReportsView />,
      },
      {
        path: 'rigs',
        element: <RigsView />,
      },
      {
        path: 'formation-tops',
        element: <FormationTopsView />,
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute anyPermission={['admin:settings', 'admin:users']}>
            <SettingsView />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

// Router component for use in providers
export const AppRouter = () => <RouterProvider router={router} />;
